import t from '@jetshop/intl';
import React, { useState, useContext } from 'react';
import { styled } from 'linaria/react';
import { LocationStateContext } from './StoreLocator';
import UserLocation from './UserLocation';
import { theme } from '../Theme';
import { StoreMarker } from './StoreMarker';

const StoreListContainer = styled('ul')`
  height: 100%;
  .highlight {
    font-weight: bold;
    text-decoration: underline;
  }

  img {
    max-width: 100%;
    height: auto;
  }
`;

const StoreContainer = styled('li')`
  width: 100%;
  background: #f5f5f5;
  padding: 20px;

  font-size: 18px;
  line-height: 23px;
  letter-spacing: 0.03em;

  outline: 1px solid transparent;
  order: ${props => (props.distance ? props.distance : 1)};
  &:focus {
    outline: none;
  }
  transition: all 0.3s;
  &:hover {
    background: #f5f5f555;
    // outline: 1px solid ${theme.colors.accent};
    box-shadow: 0px 3px 5px rgba(0, 0, 0, 0.3);

    span {
      color: black;
    }
  }
`;

const StoreColumn = styled('div')`
  position: relative;
  width: 100%;
  display: block;
  padding-bottom: 10px;

  > div p,
  .distance {
    font-style: normal;
    font-weight: normal;
    font-size: 13px;
    line-height: 15px;
    letter-spacing: 0.03em;
    margin-bottom: 7px;
  }

  > div {
    margin-bottom: 20px;
  }

  .show-open-hours {
    display: block;
    font-size: 14px;
    text-decoration: underline;
    margin: 0 0 5px 0;
    padding: 0;
    background: none;
    border: 0;
    color: ${theme.colors.black};
    &:hover {
      color: ${theme.colors.accent};
    }
    &:focus {
      outline: none;
    }
  }

  .open-hours {
    font-size: 12px;
    p,
    span {
      color: ${theme.colors.black};
    }
    h3 {
      font-size: 14px;
    }
    a {
      color: ${theme.colors.accent};
    }
  }
`;

const StoresWrapper = styled('div')`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-gap: 4px;
  margin-top: 1px;

  ${theme.below.lg} {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const StoreName = styled('p')`
  margin-bottom: 20px;
  font-style: normal;
  font-weight: normal;
  font-size: 21px;
  line-height: 25px;
  letter-spacing: 0.03em;
`;

const Phone = styled('div')`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  font-style: normal;
  font-weight: normal;
  font-size: 13px;
  line-height: 15px;
  letter-spacing: 0.03em;
  margin-bottom: 7px;

  p {
    margin-right: 5px;
  }

  p,
  a {
    text-decoration: none;
    color: black;
    align-items: center;

    p {
      margin: 0;
    }
  }
`;

const humanDistance = distance =>
  distance > 10
    ? t('{km} km', { km: Math.round(distance) })
    : distance > 1
    ? t('{km} km', { km: distance.toFixed(1) })
    : t('{m} m', { m: Math.round(distance * 1000) });

const Store = ({ storeNr, distances, store, setSelectedStoreId }) => {
  const { id, name, address1, openHours, contact, city } = store;
  const locationState = useContext(LocationStateContext);
  const active = id === locationState.activeLocation;
  const storeDistance = distances[id];
  const [open, setOpen] = useState(false);

  const stripTags = content => {
    const tagRegex = /(<([^>]+)>)/gi;
    const squareBracketsRegex = /(\[.*?\])/gi;
    const withoutSqB = content.replace(squareBracketsRegex, '');
    const withoutTag = withoutSqB.replace(tagRegex, '');
    const withoutSpace = withoutTag.replace(/\s/g, '');
    return withoutSpace;
  };

  const handleClick = id => {
    setSelectedStoreId(id);
    if (typeof window !== 'undefined') {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  return (
    <StoreContainer
      distance={parseInt(storeDistance)}
      onMouseEnter={() => locationState.setHighlightedLocation(id)}
      onMouseLeave={() => locationState.setHighlightedLocation(null)}
      tabIndex={0}
    >
      <StoreColumn>
        <StoreName dangerouslySetInnerHTML={{ __html: name }} />
        <div>
          <p
            className={'info'}
            dangerouslySetInnerHTML={{ __html: address1 }}
          />
          <p className={'info'} dangerouslySetInnerHTML={{ __html: city }} />
        </div>
        {contact && (
          <Phone>
            <p style={{ marginBottom: '0px' }}>{t('Phone')}:</p>
            <a
              href={'tel:' + stripTags(contact)}
              dangerouslySetInnerHTML={{ __html: contact }}
            />
          </Phone>
        )}
        {storeDistance && (
          <p className={'distance'}>
            {t('Distance')}: {humanDistance(storeDistance)}
          </p>
        )}
        <StoreMarker
          onClick={() => handleClick(id)}
          nr={storeNr}
          active={active}
          style={{ marginBottom: '0' }}
        />

        {openHours ? (
          <>
            <button className="show-open-hours" onClick={() => setOpen(!open)}>
              Vis åpningstider
            </button>

            {open ? (
              <div
                className="open-hours"
                dangerouslySetInnerHTML={{ __html: openHours }}
              />
            ) : null}
          </>
        ) : null}
      </StoreColumn>
    </StoreContainer>
  );
};

const StoreList = ({
  stores,
  setUserLocation,
  distances,
  setSelectedStoreId
}) => {
  return (
    <StoreListContainer>
      <UserLocation setUserLocation={setUserLocation} />
      <StoresWrapper>
        {stores &&
          stores.map((store, index) => {
            if (store.id === 25) return null;
            return (
              <Store
                setSelectedStoreId={setSelectedStoreId}
                key={index}
                storeNr={index + 1}
                store={store}
                distances={distances}
              />
            );
          })}
      </StoresWrapper>
    </StoreListContainer>
  );
};

export default StoreList;
